import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import API from '../../api/axios';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiSmartphone, FiCheck, FiX, FiLoader, FiInfo } from 'react-icons/fi';

const PROVIDER_PREFIXES = {
  '081': 'M-Pesa', '082': 'M-Pesa', '083': 'M-Pesa',
  '097': 'Airtel Money', '099': 'Airtel Money',
  '084': 'Orange Money', '085': 'Orange Money', '089': 'Orange Money',
  '090': 'Africell', '091': 'Africell',
};

const PROVIDER_LOGOS = {
  'M-Pesa': '/mpesa.webp',
  'Airtel Money': '/airtelmoney.jpg',
  'Orange Money': '/orangemoney.png',
  'Africell': '/afrimoney.webp',
};

const detectProvider = (phone) => {
  const cleaned = phone.replace(/[\s\-\(\)\+]/g, '');
  let digits = cleaned;
  if (digits.startsWith('243') && digits.length >= 12) digits = '0' + digits.slice(3);
  if (digits.length >= 3) {
    const prefix = digits.substring(0, 3);
    return PROVIDER_PREFIXES[prefix] || null;
  }
  return null;
};

const PaymentPage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [detectedOp, setDetectedOp] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await API.get(`/invoices/${invoiceId}`);
        const inv = res.data.data.invoice;
        setInvoice(inv);
        const remaining = parseFloat(inv.amount) - parseFloat(inv.amount_paid);
        setAmount(remaining.toString());
      } catch (err) {
        console.error(err);
        toast.error('Facture non trouvée.');
        navigate('/patient/invoices');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [invoiceId, navigate]);

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setPhoneNumber(val);
    setDetectedOp(detectProvider(val));
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) {
      toast.warn('Entrez un numéro de téléphone valide (10 chiffres).');
      return;
    }
    if (!detectedOp) {
      toast.warn('Numéro non reconnu. Utilisez un numéro M-Pesa, Airtel, Orange ou Africell.');
      return;
    }

    setPaying(true);
    setPaymentResult(null);

    try {
      const res = await API.post('/payments/initiate', {
        invoice_id: invoiceId,
        phone_number: phoneNumber,
        amount: parseFloat(amount),
      });

      if (res.data.success) {
        setPaymentResult({
          success: true,
          message: res.data.message,
          reference: res.data.data.payment.payment_reference,
          provider: res.data.data.payment.provider,
          fee: res.data.data.payment.fee,
        });
        toast.success('Paiement initié ! Validez le push USSD sur votre téléphone.');
      } else {
        setPaymentResult({ success: false, message: res.data.message });
        toast.error(res.data.message);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors du paiement.';
      setPaymentResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return <div className="loading-page"><div className="spinner"></div><p>Chargement...</p></div>;
  }

  if (!invoice) return null;

  const remaining = parseFloat(invoice.amount) - parseFloat(invoice.amount_paid);

  if (paymentResult) {
    return (
      <div style={{ maxWidth: 500, margin: '40px auto', textAlign: 'center' }}>
        <div className="card" style={{ padding: 40 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', margin: '0 auto 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: paymentResult.success ? 'var(--success-light)' : 'var(--danger-light)',
          }}>
            {paymentResult.success
              ? <FiCheck size={36} color="var(--success)" />
              : <FiX size={36} color="var(--danger)" />
            }
          </div>
          <h2 style={{ fontSize: 22, marginBottom: 8 }}>
            {paymentResult.success ? 'Paiement initié !' : 'Paiement échoué'}
          </h2>
          <p style={{ color: 'var(--gray-500)', marginBottom: 20 }}>{paymentResult.message}</p>

          {paymentResult.success && (
            <div className="alert alert-info" style={{ textAlign: 'left', marginBottom: 20 }}>
              <FiInfo /> Validez le push USSD sur votre téléphone pour confirmer le paiement.
            </div>
          )}

          {paymentResult.reference && (
            <div style={{ background: 'var(--gray-50)', padding: 16, borderRadius: 8, marginBottom: 12 }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Référence</p>
              <p style={{ fontWeight: 700, fontSize: 16 }}>{paymentResult.reference}</p>
            </div>
          )}

          {paymentResult.provider && (
            <div style={{ background: 'var(--gray-50)', padding: 12, borderRadius: 8, marginBottom: 20 }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Opérateur détecté</p>
              <p style={{ fontWeight: 600 }}>{paymentResult.provider}</p>
              {paymentResult.fee > 0 && (
                <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                  Frais de transaction : {paymentResult.fee} {invoice.currency}
                </p>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Link to={`/patient/invoices/${invoiceId}`} className="btn btn-outline">
              Voir la facture
            </Link>
            <Link to="/patient/invoices" className="btn btn-primary">
              Mes factures
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <Link to={`/patient/invoices/${invoiceId}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, color: 'var(--gray-600)', fontSize: 14 }}>
        <FiArrowLeft /> Retour à la facture
      </Link>

      {/* Résumé facture */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="payment-summary">
          <div>
            <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Facture</p>
            <p style={{ fontWeight: 700 }}>{invoice.invoice_number}</p>
            <p style={{ fontSize: 13, color: 'var(--gray-600)' }}>{invoice.motif}</p>
          </div>
          <div className="payment-summary-amount">
            <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Montant à payer</p>
            <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>
              {remaining.toLocaleString()} {invoice.currency}
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire paiement */}
      <div className="card">
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
          <FiSmartphone style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Paiement Mobile Money
        </h3>

        <div className="alert alert-info" style={{ marginBottom: 20 }}>
          <FiInfo /> L'opérateur (M-Pesa, Airtel, Orange, Africell) est détecté automatiquement à partir de votre numéro.
        </div>

        <form onSubmit={handlePayment}>
          <div className="form-group">
            <label className="form-label">Numéro de téléphone Mobile Money *</label>
            <input
              type="tel"
              className="form-input"
              placeholder="0891234567 (10 chiffres)"
              value={phoneNumber}
              onChange={handlePhoneChange}
              required
              style={{ fontSize: 18, letterSpacing: 1 }}
            />
            {/* Afficher l'opérateur détecté */}
            {phoneNumber.replace(/\D/g, '').length >= 3 && (
              <div style={{
                marginTop: 8, padding: '8px 12px', borderRadius: 6,
                background: detectedOp ? 'var(--success-light)' : 'var(--danger-light)',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                {detectedOp ? (
                  <>
                    <img
                      src={PROVIDER_LOGOS[detectedOp]}
                      alt={detectedOp}
                      style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
                    />
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{detectedOp}</span>
                    <FiCheck color="var(--success)" />
                  </>
                ) : (
                  <span style={{ fontSize: 13, color: 'var(--danger)' }}>
                    Préfixe non reconnu. Utilisez : 081-083 (M-Pesa), 084-085/089 (Orange), 097/099 (Airtel), 090-091 (Africell)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Grille opérateurs pour info */}
          <div style={{ marginBottom: 20 }}>
            <label className="form-label" style={{ fontSize: 12, color: 'var(--gray-500)' }}>Opérateurs supportés</label>
            <div className="provider-grid">
              {[
                { name: 'M-Pesa', prefixes: '081, 082, 083' },
                { name: 'Orange Money', prefixes: '084, 085, 089' },
                { name: 'Airtel Money', prefixes: '097, 099' },
                { name: 'Africell', prefixes: '090, 091' },
              ].map((p) => (
                <div key={p.name} className={`provider-card ${detectedOp === p.name ? 'selected' : ''}`}
                  style={{ cursor: 'default', opacity: detectedOp && detectedOp !== p.name ? 0.4 : 1 }}>
                  <img
                    src={PROVIDER_LOGOS[p.name]}
                    alt={p.name}
                    style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', margin: '0 auto', display: 'block' }}
                  />
                  <div className="provider-name">{p.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{p.prefixes}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Montant ({invoice.currency})</label>
            <input
              type="number"
              className="form-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="1"
              max={remaining}
              step="0.01"
              required
            />
            <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
              Maximum : {remaining.toLocaleString()} {invoice.currency}
            </p>
          </div>

          <button
            type="submit"
            className="btn btn-success btn-lg"
            style={{ width: '100%' }}
            disabled={paying || !detectedOp}
          >
            {paying ? (
              <><FiLoader className="spin" /> Envoi du push USSD...</>
            ) : (
              `Payer ${parseFloat(amount || 0).toLocaleString()} ${invoice.currency} via ${detectedOp || 'Mobile Money'}`
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PaymentPage;
